/* The workbench — the `build` unit player (CR-020, laid out in Milestone B).
 *
 * The loop is: read the goal, commit to a prediction, run, watch it behave the
 * way you said or not, fix it, run again. Two things decide whether that loop
 * actually closes on a phone, and both are layout, not logic:
 *
 *   - the results sit DIRECTLY under the editor, so a failing check and the
 *     line that caused it are never more than a thumb-flick apart;
 *   - a sticky action bar keeps Run and the verdict reachable with the iOS
 *     keyboard open, which leaves about 420px of viewport.
 *
 * Credit is earned by the work: green checks. The prediction is still taken
 * once, before the first run, because it is the only way to get an honest
 * forecast — but it reports, it does not gate. Predicting "this will fail" on a
 * deliberately broken starter is a CORRECT call, and an earlier version of this
 * screen made that the one thing that locked you out of credit forever.
 *
 * His code lives in IndexedDB and never reaches the server (ADR-02).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CODE_RUN_TIMEOUT_MS, PYODIDE_BASE } from '@ascent/shared';
import type { UnitDto } from '../api';
import { Markdown } from '../markdown';
import { runInWorker } from '../exercise/runner';
import { pythonRunnable } from '../exercise/pack';
import type { RunOutcome } from '../exercise/types';
import { indent, outdent, newline, insertText, type EditState } from '../editor-keys';
import { loadDraft, saveDraft, type BuildDraft, type RunRecord } from '../build-store';

type PredictionOption = NonNullable<UnitDto['prediction']>['options'][number];

/* The characters an iOS keyboard buries two layers deep, and the ones Python
 * needs most. Inserted at the cursor, so they cost one tap instead of three. */
const KEY_ROW = [':', '_', '(', ')', '[', ']', '"', '=', '.'];

/* Content names a check by its Python function name (`test_two_workers…`),
 * which is what the validator can verify against checks_py. The runner reports
 * the display label the harness derives from it ("two workers…"), so both sides
 * are normalised before comparing. */
const normaliseCheck = (s: string) =>
  s.replace(/^(test|check)_/, '').replace(/_/g, ' ').trim().toLowerCase();

/** Which option the run actually vindicated. Pure function of the outcome. */
function optionMatchingRun(options: PredictionOption[], outcome: RunOutcome): string | null {
  if (outcome.status !== 'ok') return null;
  const failed = outcome.checks.filter((c) => !c.passed).map((c) => normaliseCheck(c.name));
  const allPass = outcome.passed;

  // An option naming a specific check is more precise than a bare all_pass /
  // any_fail, so it wins when both would match.
  const named = options.find(
    (o) => o.asserts.check && failed.includes(normaliseCheck(o.asserts.check)),
  );
  if (named && !allPass) return named.id;

  const bare = options.find(
    (o) => !o.asserts.check && (o.asserts.outcome === 'all_pass') === allPass,
  );
  if (bare) return bare.id;

  return options.find((o) => (o.asserts.outcome === 'all_pass') === allPass)?.id ?? null;
}

/** One line, for the sticky bar: what the last run said, without scrolling. */
function verdictLine(outcome: RunOutcome | null): string | null {
  if (!outcome) return null;
  if (outcome.status === 'timeout') return `Stopped after ${CODE_RUN_TIMEOUT_MS / 1000}s`;
  if (outcome.status === 'error') return 'It raised — see the traceback';
  if (outcome.status === 'blocked') return 'Python needs the offline pack';
  const failed = outcome.checks.filter((c) => !c.passed).length;
  if (failed === 0) return `All ${outcome.checks.length} checks pass`;
  return `${failed} of ${outcome.checks.length} checks failing`;
}

export function BuildScreen({ unit, onBack }: { unit: UnitDto; onBack: () => void }) {
  const options = useMemo(() => unit.prediction?.options ?? [], [unit]);
  const stem = unit.prediction?.stem_md ?? '';
  const starter = unit.starter_code ?? '';
  const checksPy = unit.checks_py ?? '';
  const seedSql = unit.seed_sql;
  const goal = unit.goal_md ?? unit.body_md;
  const referenceNote = unit.reference_note_md;

  const [code, setCode] = useState(starter);
  const [prediction, setPrediction] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [running, setRunning] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [goalOpen, setGoalOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [packOk, setPackOk] = useState(true);

  const editor = useRef<HTMLTextAreaElement | null>(null);
  const results = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void (async () => {
      const found = await loadDraft(unit.id);
      setCode(found?.code ?? starter);
      setPrediction(found?.prediction ?? null);
      setRuns(found?.runs ?? []);
      // The goal is worth the whole screen the first time and worth a line
      // after that; once you have run, you are debugging, not reading.
      setGoalOpen((found?.runs?.length ?? 0) === 0);
      setPackOk(await pythonRunnable());
    })();
  }, [unit.id, starter]);

  const persist = useCallback(
    (next: Partial<BuildDraft>) => {
      void saveDraft({
        unitId: unit.id,
        code,
        prediction,
        runs,
        updatedAt: new Date().toISOString(),
        ...next,
      });
    },
    [unit.id, code, prediction, runs],
  );

  const passed = runs.some((r) => r.passed);
  // Credit is the work. The forecast is reported beside it, never inside it.
  const credited = passed;
  const firstRun = runs.find((r) => r.predicted !== null) ?? null;

  /* Apply a pure edit and put the caret back where the rule says it goes.
   * React re-renders from `value`, which resets the selection, so restoring it
   * has to happen after paint. */
  const applyEdit = useCallback((next: EditState) => {
    setCode(next.value);
    persist({ code: next.value });
    requestAnimationFrame(() => {
      const el = editor.current;
      if (!el) return;
      el.selectionStart = next.selectionStart;
      el.selectionEnd = next.selectionEnd;
    });
  }, [persist]);

  const stateOf = (el: HTMLTextAreaElement): EditState => ({
    value: el.value,
    selectionStart: el.selectionStart ?? 0,
    selectionEnd: el.selectionEnd ?? 0,
  });

  const run = useCallback(async () => {
    if (running || !prediction) return;
    setRunning(true);
    setOutcome(null);
    const result = await runInWorker('python', {
      kind: 'python',
      code,
      checksPy,
      base: PYODIDE_BASE,
      ...(seedSql ? { seedSql } : {}),
    });
    setOutcome(result);

    const isFirst = runs.length === 0;
    const vindicated = optionMatchingRun(options, result);
    const record: RunRecord = {
      at: new Date().toISOString(),
      passed: result.status === 'ok' && result.passed,
      predicted: isFirst ? prediction : null,
      // Scored on the first run only: after that you have seen the behaviour,
      // so a "prediction" would be a memory rather than a forecast.
      predictionCorrect: isFirst && vindicated !== null ? vindicated === prediction : null,
      failedChecks:
        result.status === 'ok' ? result.checks.filter((c) => !c.passed).map((c) => c.name) : [],
    };
    const nextRuns = [...runs, record];
    setRuns(nextRuns);
    persist({ code, prediction, runs: nextRuns });
    if (isFirst) setGoalOpen(false);
    setRunning(false);
  }, [running, prediction, code, checksPy, seedSql, options, runs, persist]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void run();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      applyEdit(e.shiftKey ? outdent(stateOf(el)) : indent(stateOf(el)));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      applyEdit(newline(stateOf(el)));
      return;
    }
    if (e.key === 'Escape') el.blur();
  };

  const verdict = verdictLine(outcome);

  return (
    <main className="screen build">
      <button type="button" className="back" onClick={onBack}>← Back</button>
      <p className="eyebrow">Build{unit.proof ? ' · proof' : ''}</p>
      <h1>{unit.title}</h1>

      {/* --- Goal, collapsible ---------------------------------------------- */}
      <section className="build__step">
        <button
          type="button"
          className="sources__toggle"
          onClick={() => setGoalOpen((v) => !v)}
          aria-expanded={goalOpen}
        >
          The task {goalOpen ? '▾' : '▸'}
        </button>
        {goalOpen ? <div className="card"><Markdown source={goal} /></div> : null}
      </section>

      {/* --- 1. Predict ------------------------------------------------------ */}
      <section className="build__step">
        <p className="runner__label">1 · Predict</p>
        {prediction === null ? (
          <div className="card">
            <Markdown source={stem} />
            <p className="hint">
              One tap, before you run. This is a measurement, not a test — it does
              not affect whether you get credit.
            </p>
            <div className="build__options">
              {options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="build__option"
                  onClick={() => { setPrediction(o.id); persist({ prediction: o.id }); }}
                >
                  <Markdown source={o.label_md} />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="card build__said">
            <p className="hint" style={{ margin: 0 }}>You said</p>
            <Markdown source={options.find((o) => o.id === prediction)?.label_md ?? prediction} />
            {firstRun?.predictionCorrect === true ? (
              <p className="hint">You called it.</p>
            ) : firstRun?.predictionCorrect === false ? (
              <p className="hint">
                Not what happened — that gap is the useful part, and it costs you
                nothing here.
              </p>
            ) : null}
          </div>
        )}
      </section>

      {/* --- 2. Build -------------------------------------------------------- */}
      <section className="build__step">
        <p className="runner__label">2 · Build</p>
        <textarea
          ref={editor}
          className="runner__editor runner__editor--tall"
          value={code}
          rows={14}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          onKeyDown={onKeyDown}
          onFocus={() => setEditing(true)}
          onBlur={() => { setEditing(false); persist({ code }); }}
          onChange={(e) => { setCode(e.target.value); persist({ code: e.target.value }); }}
          aria-label="Your code"
        />
        {editing ? (
          <div className="keyrow" role="toolbar" aria-label="Insert">
            {KEY_ROW.map((k) => (
              <button
                key={k}
                type="button"
                className="keyrow__key"
                // Keep focus in the textarea so the caret and keyboard stay put.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const el = editor.current;
                  if (el) applyEdit(insertText(stateOf(el), k));
                }}
              >
                {k}
              </button>
            ))}
            <button
              type="button"
              className="keyrow__key keyrow__key--wide"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { const el = editor.current; if (el) applyEdit(indent(stateOf(el))); }}
            >
              tab
            </button>
          </div>
        ) : null}
        <p className="hint">
          Tab indents, Shift+Tab outdents, Enter keeps your indentation.
          {runs.length > 0 ? ` Attempt ${runs.length} — running again costs nothing.` : ''}
        </p>
      </section>

      {/* --- 3. What happened ------------------------------------------------ */}
      <div ref={results} />
      {outcome ? (
        <section className="build__step">
          <p className="runner__label">3 · What happened</p>
          {outcome.status === 'timeout' ? (
            <div className="card card--blocked">
              <div className="card__title"><span>Stopped after {CODE_RUN_TIMEOUT_MS / 1000} seconds</span></div>
              <p style={{ margin: 0 }}>
                Something did not finish — an unbounded loop, or a query with no end to it.
              </p>
            </div>
          ) : outcome.status === 'error' ? (
            <div className="card card--blocked">
              <div className="card__title"><span>It raised</span></div>
              <pre className="runner__error">{outcome.message}</pre>
            </div>
          ) : outcome.status === 'blocked' ? (
            <div className="card card--blocked">
              <div className="card__title"><span>Python needs the offline pack</span></div>
              <p style={{ margin: 0 }}>
                You are offline and the runtime is not cached on this device.
                Settings → Offline pack, once you are back online.
              </p>
            </div>
          ) : (
            <>
              <ul className="checks">
                {outcome.checks.map((c, i) => (
                  <li key={`${c.name}-${i}`} className={c.passed ? 'checks--ok' : 'checks--no'}>
                    <span className="checks__mark">{c.passed ? 'pass' : 'fail'}</span>
                    <span className="checks__name">{c.name}</span>
                    <span className="checks__msg">{c.message}</span>
                  </li>
                ))}
              </ul>
              {outcome.stdout.trim() ? (
                <>
                  <p className="runner__label">Output</p>
                  <pre className="runner__stdout">{outcome.stdout}</pre>
                </>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {/* --- 4. Credit ------------------------------------------------------- */}
      {runs.length > 0 ? (
        <section className="build__step">
          <p className="runner__label">4 · Credit</p>
          <div className={`card ${credited ? 'card--ok' : ''}`}>
            {credited ? (
              <p style={{ margin: 0 }}><strong>Done.</strong> It works.</p>
            ) : (
              <p style={{ margin: 0 }}>
                Not yet — the failing checks above say what is still wrong.
              </p>
            )}
          </div>
        </section>
      ) : null}

      {/* --- Reference ------------------------------------------------------- */}
      {runs.length > 0 ? (
        <section className="build__step">
          <button type="button" className="sources__toggle" onClick={() => setShowReference((v) => !v)}>
            Show a reference implementation {showReference ? '▾' : '▸'}
          </button>
          {showReference ? (
            <div className="card">
              <p className="hint" style={{ marginTop: 0 }}>
                Not the answer — one way that works. Yours is the one that carries
                forward; take from this what you want.
              </p>
              {referenceNote ? <Markdown source={referenceNote} /> : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <p className="hint">
        Your code stays on this device — saved in the browser, never sent to the server.
      </p>

      {/* --- Sticky bar: the thing that makes the loop close on a phone ------ */}
      <div className="runbar">
        {verdict ? (
          <button
            type="button"
            className={`runbar__verdict ${passed ? 'runbar__verdict--ok' : ''}`}
            onClick={() => results.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {verdict}
          </button>
        ) : null}
        <div className="runbar__actions">
          <button
            type="button"
            onClick={() => void run()}
            disabled={running || prediction === null || !packOk}
          >
            {running ? 'Running…' : runs.length === 0 ? 'Run' : 'Run again'}
          </button>
          <button
            type="button"
            className="runner__reset"
            onClick={() => { setCode(starter); persist({ code: starter }); }}
          >
            Reset
          </button>
        </div>
        {prediction === null ? (
          <p className="runbar__hint">Make your prediction first — one tap, above.</p>
        ) : !packOk ? (
          <p className="runbar__hint">Offline and no pack — Settings → Offline pack.</p>
        ) : null}
      </div>
    </main>
  );
}
